import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  MessageCircle, 
  Send, 
  Loader2, 
  Bot,
  User,
  Sparkles,
  X,
  Minimize2,
  Maximize2,
  Eye
} from 'lucide-react';

const HIDDEN_STORAGE_KEY = 'holly_chat_hidden';

const HollyChat = ({ isHomeSmart = false }) => {
  const sessionIdRef = useRef(
    sessionStorage.getItem('holly_responsible_session') ||
    (window.crypto?.randomUUID?.() || `holly-${Date.now()}`)
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isHidden, setIsHidden] = useState(() => {
    try {
      return localStorage.getItem(HIDDEN_STORAGE_KEY) === 'true';
    } catch (e) {
      return false;
    }
  });

  const hideMascot = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    try {
      localStorage.setItem(HIDDEN_STORAGE_KEY, 'true');
    } catch (err) {
      /* ignore */
    }
    setIsHidden(true);
  };

  const showMascot = () => {
    try {
      localStorage.setItem(HIDDEN_STORAGE_KEY, 'false');
    } catch (err) {
      /* ignore */
    }
    setIsHidden(false);
  };
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: isHomeSmart
        ? 'Ciao! Sono Holly, la tua assistente Home Smart. Posso aiutarti con i tuoi immobili, prenotazioni, chiusure, report e performance.'
        : 'Ciao! Sono Holly, la tua assistente per il portale HWsmart. Come posso aiutarti oggi? Puoi chiedermi qualsiasi cosa sulla piattaforma: arrivi e partenze, immobili, formazione, e molto altro!'
    }
  ]);

  useEffect(() => {
    setMessages((current) => {
      if (current.length !== 1 || current[0]?.role !== 'assistant') return current;
      return [{
        role: 'assistant',
        content: isHomeSmart
          ? 'Ciao! Sono Holly, la tua assistente Home Smart. Posso aiutarti con i tuoi immobili, prenotazioni, chiusure, report e performance.'
          : 'Ciao! Sono Holly, la tua assistente per il portale HWsmart. Come posso aiutarti oggi? Puoi chiedermi qualsiasi cosa sulla piattaforma: arrivi e partenze, immobili, formazione, e molto altro!'
      }];
    });
  }, [isHomeSmart]);

  useEffect(() => {
    sessionStorage.setItem('holly_responsible_session', sessionIdRef.current);
  }, []);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isHomeSmart) return undefined;
    const listener = (event) => {
      setIsHidden(false);
      localStorage.setItem(HIDDEN_STORAGE_KEY, 'false');
      setIsOpen(true);
      setIsMinimized(false);
      setInput(event.detail?.prompt || 'Rivedi la mia configurazione Home Smart.');
    };
    window.addEventListener('home-smart:open-holly', listener);
    return () => window.removeEventListener('home-smart:open-holly', listener);
  }, [isHomeSmart]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/api/holly/responsabile/chat`,
        { message: userMessage, session_id: sessionIdRef.current, history: messages.slice(-8) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Mi dispiace, si è verificato un errore. Riprova tra qualche istante.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Hidden state: show only a small restore icon
  if (isHidden) {
    return (
      <button
        onClick={showMascot}
        title="Mostra Holly"
        data-testid="holly-chat-restore-button"
        className={`fixed bottom-3 right-3 z-50 w-8 h-8 text-white rounded-full shadow-md flex items-center justify-center opacity-50 hover:opacity-100 transition-[opacity,background-color] ${isHomeSmart ? 'bg-blue-700/80 hover:bg-blue-800' : 'bg-purple-500/70 hover:bg-purple-600'}`}
      >
        <Eye className="h-4 w-4" />
      </button>
    );
  }

  // Floating button when closed
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <div className="relative">
          <button
            onClick={() => setIsOpen(true)}
            className={`${isHomeSmart ? 'bg-blue-700 hover:bg-blue-800' : 'bg-purple-600 hover:bg-purple-700'} text-white rounded-full p-4 shadow-lg transition-[transform,background-color] hover:scale-110`}
            data-testid="holly-chat-button"
          >
            <MessageCircle className="h-6 w-6" />
            <span className="absolute -top-1 -right-1 bg-green-500 rounded-full w-3 h-3 animate-pulse"></span>
          </button>
          {/* Hide button */}
          <button
            onClick={hideMascot}
            title="Nascondi Holly"
            data-testid="holly-chat-hide-button"
            className="absolute -top-2 -left-2 w-6 h-6 bg-white hover:bg-red-50 text-gray-700 hover:text-red-600 border border-gray-300 rounded-full shadow-md flex items-center justify-center z-10 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <div 
        className={`fixed bottom-6 right-6 ${isHomeSmart ? 'bg-blue-700' : 'bg-purple-600'} text-white rounded-full px-4 py-2 shadow-lg z-50 cursor-pointer flex items-center gap-2`}
        onClick={() => setIsMinimized(false)}
        data-testid="holly-chat-minimized-button"
      >
        <Bot className="h-5 w-5" />
        <span className="font-medium">Holly</span>
        <Maximize2 className="h-4 w-4 ml-2" />
      </div>
    );
  }

  // Full chat window
  return (
    <div className="fixed bottom-4 right-4 w-[calc(100vw-2rem)] max-w-96 z-50 sm:bottom-6 sm:right-6" data-testid="holly-chat-window">
        <Card className={`shadow-2xl ${isHomeSmart ? 'border-blue-200' : 'border-purple-200'}`}>
        {/* Header */}
        <CardHeader className={`${isHomeSmart ? 'bg-gradient-to-r from-blue-800 to-teal-700' : 'bg-gradient-to-r from-purple-600 to-indigo-600'} text-white rounded-t-lg py-3 px-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-full p-2">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold" data-testid="holly-chat-title">Holly</CardTitle>
                <p className={`text-xs ${isHomeSmart ? 'text-blue-100' : 'text-purple-100'}`} data-testid="holly-chat-brand-subtitle">{isHomeSmart ? 'Assistente Home Smart' : 'Assistente HWsmart'}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setIsMinimized(true)}
                className="p-1 hover:bg-white/20 rounded"
                data-testid="holly-minimize-button"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded"
                data-testid="holly-close-button"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </CardHeader>

        {/* Messages */}
        <CardContent className="p-0">
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((msg, idx) => (
              <div 
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`holly-chat-message-${idx}`}
              >
                <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`rounded-full p-1.5 flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-blue-100' : 'bg-purple-100'
                  }`}>
                    {msg.role === 'user' ? (
                      <User className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Sparkles className="h-4 w-4 text-purple-600" />
                    )}
                  </div>
                  <div className={`rounded-2xl px-4 py-2 ${
                    msg.role === 'user' 
                      ? 'bg-blue-600 text-white rounded-br-md' 
                      : 'bg-white text-slate-800 shadow-sm rounded-bl-md'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{msg.content}</p>
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2">
                  <div className="bg-purple-100 rounded-full p-1.5">
                    <Sparkles className="h-4 w-4 text-purple-600" />
                  </div>
                  <div className="bg-white rounded-2xl px-4 py-2 shadow-sm">
                    <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t bg-white rounded-b-lg">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Scrivi un messaggio..."
                className="flex-1"
                disabled={loading}
                data-testid="holly-chat-input"
              />
              <Button 
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className={isHomeSmart ? 'bg-blue-700 hover:bg-blue-800' : 'bg-purple-600 hover:bg-purple-700'}
                data-testid="holly-send-button"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2 text-center" data-testid="holly-chat-privacy-note">
              {isHomeSmart ? 'Holly usa solo i dati del tuo account Home Smart' : 'Holly può aiutarti con il portale HWsmart'}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HollyChat;
